import { Suspense, useEffect } from 'react';
import { Outlet } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { I18nextProvider } from 'react-i18next';
import { ThemeProvider } from 'next-themes';
import { TooltipProvider } from '@/components/ui/tooltip';
import { Toaster } from '@/components/ui/toaster';
import { Toaster as Sonner } from '@/components/ui/sonner';
import { CookieConsent } from '@/components/CookieConsent';
import { LanguageSuggestionBanner } from '@/components/LanguageSuggestionBanner';

import i18n, { restoreSavedLanguage } from '@/config/i18n';
import { useTranslation } from 'react-i18next';
import HreflangTags from '@/components/HreflangTags';
import RouteTracker from '@/components/RouteTracker';
import { AuthProvider } from '@/hooks/useAuth';
import { UserPreferencesProvider } from '@/hooks/useUserPreferences';
import { SchemaScript, generateOrganizationSchema, generateWebSiteSchema } from '@/lib/seoHelpers';

const queryClient = new QueryClient();

export default function RootLayout() {
  const { i18n: i18nInstance } = useTranslation();

  // Restore saved language after hydration to avoid mismatch
  useEffect(() => {
    restoreSavedLanguage();
  }, []);

  // Keep <html dir> in sync with current language (lang is set by HreflangTags via Helmet)
  useEffect(() => {
    document.documentElement.dir = i18nInstance.language === 'ar' ? 'rtl' : 'ltr';
  }, [i18nInstance.language]);

  return (
    <ThemeProvider attribute="class" defaultTheme="dark" storageKey="cctvplanner_theme" enableSystem={false}>
      <I18nextProvider i18n={i18n}>
        <QueryClientProvider client={queryClient}>
          <AuthProvider>
            <UserPreferencesProvider>
              <TooltipProvider>
                <Toaster />
                <Sonner />
                <HreflangTags />
                <SchemaScript schema={generateOrganizationSchema()} />
                <SchemaScript schema={generateWebSiteSchema()} />
                <RouteTracker />
                <LanguageSuggestionBanner />
                <Suspense fallback={<div className="min-h-screen" />}>
                  <Outlet />
                </Suspense>
                <CookieConsent />
              </TooltipProvider>
            </UserPreferencesProvider>
          </AuthProvider>
        </QueryClientProvider>
      </I18nextProvider>
    </ThemeProvider>
  );
}
